import { useCallback, useEffect } from 'react'
import { usePlaidLink, type PlaidLinkOnSuccess } from 'react-plaid-link'

import Button from 'components/core/button'
import { BankAccount } from 'interfaces/payment-method'
import { cn } from 'lib/util/cn'
import { useMutation, useQuery } from '@tanstack/react-query'
import { request, requestQuery } from 'lib/api/fetch-api'
import { toast } from 'sonner'

interface BankAccountFormProps {
  className?: HTMLDivElement['className']
  bankInfo: BankAccount
  setBankInfo: (bankInfo: BankAccount) => void
}

export default function BankAccountForm({ className, bankInfo, setBankInfo }: BankAccountFormProps) {
  const exchangeBillingPublicToken = useMutation({
    mutationFn: (publicToken: string) =>
      request({ endpoint: 'exchangeBillingPublicToken', body: { plaid: { public_token: publicToken } } }),
  })

  const {
    data,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['billingLinkToken'],
    queryFn: requestQuery({ endpoint: 'createBillingLinkToken' }),
  })

  useEffect(() => {
    if (error) {
      toast.error('Error connecting to Plaid')
      console.error('Error creating Plaid link token', error)
    }
  }, [error])

  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    async (publicToken, metadata) => {
      const response = await exchangeBillingPublicToken.mutateAsync(publicToken)

      const accountId = metadata['account_id']
      setBankInfo({
        accessToken: response.access_token,
        plaidAccountId: accountId,
        plaidAccountMetadata: { name: metadata.accounts?.[0]?.name ?? '', mask: metadata.accounts?.[0]?.mask ?? '' },
      })
    },
    [exchangeBillingPublicToken, setBankInfo],
  )

  const { open, ready } = usePlaidLink({ token: data?.link_token ?? null, onSuccess })

  return (
    <div className={cn('tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-4', className)}>
      {(bankInfo.plaidAccountMetadata.name || bankInfo.plaidAccountMetadata.mask) && (
        <div className="tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-neutral-400 tw-text-sm tw-font-semibold">
          <span>{bankInfo.plaidAccountMetadata?.name ?? 'Bank Account Added'}</span>
          <span className="tw-inline-flex tw-items-center tw-gap-0.5">
            <span className="tw-text-xs">••••••••••••</span>
            <span>{bankInfo.plaidAccountMetadata?.mask ?? ''}</span>
          </span>
        </div>
      )}
      <Button type="button" color="purple" onClick={() => open()} disabled={!ready || loading || !data?.link_token}>
        {loading || !ready || !data?.link_token ? (
          'Loading…'
        ) : (
          <>
            {bankInfo.plaidAccountMetadata.name || bankInfo.plaidAccountMetadata.mask
              ? 'Change Bank Account'
              : 'Replace Card with Bank Account'}
          </>
        )}
      </Button>
    </div>
  )
}
